import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Award, Printer, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";

/**
 * Client-rendered milestone certificate (Phase 8) -- shown when a student
 * completes a subject. Brand-styled and printable/shareable via the browser
 * print dialog (see the `.printing-certificate` rules in globals.css, which
 * isolate the certificate sheet from the rest of the app on print).
 *
 * Rendered through a portal on document.body so the print-isolation CSS
 * (which hides body's direct children) can cleanly show only this sheet.
 */
export function MilestoneCertificate({
  studentName,
  subjectName,
  classId,
  onClose,
}: {
  studentName: string;
  subjectName: string;
  classId: string | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const onPrint = () => {
    document.body.classList.add("printing-certificate");
    const cleanup = () => {
      document.body.classList.remove("printing-certificate");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  };

  const grade = classId ? classId.replace(/^class-/, "") : null;

  return createPortal(
    <div data-certificate-portal className="fixed inset-0 z-[80] grid place-items-center p-4">
      <div
        data-certificate-backdrop
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        data-certificate-sheet
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border-4 border-brand bg-white text-[#1F2937] shadow-2xl"
        style={{ aspectRatio: "1.414 / 1" }}
      >
        <div className="flex h-full flex-col items-center justify-center px-8 py-10 text-center">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-brand text-white">
            <Award className="h-7 w-7" />
          </div>
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.28em] text-brand">Certificate of completion</p>
          <p className="mt-6 text-sm text-[#6B7280]">This certifies that</p>
          <p className="mt-1 font-display text-3xl font-bold text-[#0D0C0C]">{studentName}</p>
          <p className="mt-4 text-sm text-[#6B7280]">has completed every chapter in</p>
          <p className="mt-1 font-display text-2xl font-semibold text-brand">
            {subjectName}{grade ? ` — Class ${grade}` : ""}
          </p>
          <p className="mt-6 text-xs text-[#6B7280]">{formatDate(new Date())}</p>
          <p className="mt-1 text-xs font-bold tracking-wide text-[#0D0C0C]">StaxED</p>
        </div>
      </div>

      <div data-certificate-noprint className="absolute right-4 top-4 flex gap-2">
        <Button variant="secondary" size="sm" onClick={onPrint}>
          <Printer className="h-4 w-4" />
          Print / Save
        </Button>
        <Button variant="secondary" size="icon" aria-label="Close" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>,
    document.body,
  );
}
